import React, { useMemo } from "react";
import { Box, Skeleton, useMediaQuery } from "@mui/material";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import { MachineContext } from "../../../shared/chatBot/models/chatBotModels";
import { SessionInitResponse } from "../midasPageModels";
import { Machine } from "../../machinery/models/models";
import { dt } from "../../../shared/themes/redesignTokens";

// ===================================================================
//  Design tokens (from approved Paper design)
// ===================================================================

const midas = {
  gold: "#D4A843",
  goldEnd: "#B8902F",
  statusGreen: "#27AE60",
  statusAmber: "#F39C12",
  statusRed: "#E74C3C",
  pillBorder: "#E5E7EB",
  srBlue: "#3B82F6",
  activityAmber: "#F59E0B",
  textDark: "#1a1a2e",
  textGray: "#6B7280",
  textMuted: "#9CA3AF",
  cardText: "#4B5563",
};

// ===================================================================
//  Status color logic (same as MidasFleetChips)
// ===================================================================

const getStatusColor = (machine: Machine): string => {
  const count = machine.activeServiceRequestCount || 0;
  const color = machine.serviceRequestStatusColor || "";
  if (count === 0) return midas.statusGreen;
  if (color === "red" || color === "error") return midas.statusRed;
  return midas.statusAmber;
};

// ===================================================================
//  SVG Icons (inline, design-matched)
// ===================================================================

const ReportIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 3.5h8M3 6h5M3 8.5h6" stroke={midas.gold} strokeWidth="1.3" strokeLinecap="round" />
    <rect x="1" y="1" width="12" height="12" rx="2" stroke={midas.gold} strokeWidth="1.3" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="5.5" stroke={midas.srBlue} strokeWidth="1.3" />
    <path d="M7 4v3.5l2.5 1.5" stroke={midas.srBlue} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WarningIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 2L14.5 13H1.5L8 2Z" stroke={midas.activityAmber} strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M8 6.5v3" stroke={midas.activityAmber} strokeWidth="1.3" strokeLinecap="round" />
    <circle cx="8" cy="11" r="0.6" fill={midas.activityAmber} />
  </svg>
);

const SuggestionIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M4 6l4-3 4 3M4 10l4 3 4-3" stroke={midas.gold} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ===================================================================
//  Suggestion card config (fallback when bot doesn't provide)
// ===================================================================

const fallbackSuggestions = [
  "midas.welcome.machineSuggestReports",
  "midas.welcome.machineSuggestParts",
  "midas.welcome.machineSuggestIntervals",
  "midas.welcome.machineSuggestLastMaintenance",
];

// ===================================================================
//  MidasMachineWelcome
// ===================================================================

interface MidasMachineWelcomeProps {
  machineContext: MachineContext;
  botSessionInit?: SessionInitResponse | null;
  botSessionInitLoading?: boolean;
  onSuggestionClick: (text: string) => void;
}

const MidasMachineWelcome: React.FC<MidasMachineWelcomeProps> = ({
  machineContext,
  botSessionInit,
  botSessionInitLoading,
  onSuggestionClick,
}) => {
  const { translate } = useTranslation("chatBot");
  const user = useAppSelector((s) => s.account.user);
  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);
  const isPhone = useMediaQuery("(max-width:768px)");

  const firstName = user?.firstName || "";
  const displayModel =
    machineContext.manufacturerName && machineContext.model
      ? `${machineContext.manufacturerName} ${machineContext.model}`
      : machineContext.model || "";

  // Find machine in Redux for status/counts
  const machine = useMemo(() => {
    if (!machinesMap || typeof machinesMap !== "object") return null;
    for (const group of Object.values(machinesMap)) {
      if (Array.isArray(group)) {
        const found = group.find((m: Machine) => m.id === machineContext.machineId);
        if (found) return found;
      }
    }
    return null;
  }, [machinesMap, machineContext.machineId]);

  const statusColor = machine ? getStatusColor(machine) : null;

  // Counts: bot summary or Redux fallback
  const hasBotSummary = !!botSessionInit?.summary;
  const openReports = hasBotSummary
    ? botSessionInit!.summary.open_service_reports ?? 0
    : machine?.openServiceReportsCount ?? 0;
  const activeSRs = hasBotSummary
    ? botSessionInit!.summary.open_service_requests ?? 0
    : machine?.activeServiceRequestCount ?? 0;
  const recentActivity = botSessionInit?.summary?.recent_activity ?? null;
  const greetingFromBot = botSessionInit?.greeting ?? null;

  // Specs line: serial # + control system + axis config
  const specsLine = useMemo(() => {
    const parts: string[] = [];
    if (machineContext.serialNumber) parts.push(`#${machineContext.serialNumber}`);
    if (machineContext.controlSystemBrandName) parts.push(machineContext.controlSystemBrandName);
    if (machineContext.axisConfigName) parts.push(machineContext.axisConfigName);
    return parts.join(" \u00B7 ");
  }, [machineContext]);

  // Suggestion items: bot-generated or fallback
  const suggestions = useMemo(() => {
    const botItems = botSessionInit?.suggestions;
    if (Array.isArray(botItems) && botItems.length > 0) {
      return botItems.map((text) => ({ key: text, text }));
    }
    return fallbackSuggestions.map((key) => ({
      key,
      text: translate(key),
    }));
  }, [botSessionInit?.suggestions, translate]);

  // Greeting text
  const greeting = greetingFromBot
    || translate("midas.welcome.greeting", { firstName });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        padding: isPhone ? "24px 16px" : "40px 24px",
        overflow: "auto",
        maxWidth: "520px",
        mx: "auto",
        width: "100%",
      }}
    >
      {/* 1. Machine Identity block */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
          animation: "midasWelcomeAvatar 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both",
        }}
      >
        {/* Gold MIDAS badge */}
        <Box
          sx={{
            width: "44px",
            height: "44px",
            borderRadius: "14px",
            background: `linear-gradient(135deg, ${midas.gold}, ${midas.goldEnd})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: dt.font,
              fontSize: "22px",
              fontWeight: 800,
              color: "#FFFFFF",
              lineHeight: 1,
            }}
          >
            M
          </Box>
        </Box>

        {/* Machine name + status dot */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {botSessionInitLoading ? (
            <Skeleton variant="text" width={180} sx={{ bgcolor: "rgba(0,0,0,0.06)" }} />
          ) : (
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "20px",
                fontWeight: 700,
                color: midas.textDark,
                letterSpacing: "-0.03em",
                lineHeight: 1.3,
                textAlign: "center",
              }}
            >
              {displayModel || translate("midas.welcome.machineStatus")}
            </Box>
          )}
          {statusColor && (
            <Box
              sx={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: statusColor,
                boxShadow: `0 0 4px ${statusColor}`,
                flexShrink: 0,
              }}
            />
          )}
        </Box>

        {/* Specs line */}
        {specsLine && (
          <Box
            sx={{
              fontFamily: dt.fontMono,
              fontSize: "11px",
              fontWeight: 400,
              color: midas.textMuted,
              textAlign: "center",
            }}
          >
            {specsLine}
          </Box>
        )}
      </Box>

      {/* 2. Greeting */}
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "14px",
          fontWeight: 400,
          color: midas.textGray,
          textAlign: "center",
          mt: "16px",
          animation: "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
        }}
      >
        {botSessionInitLoading ? (
          <Skeleton variant="text" width={160} sx={{ mx: "auto", bgcolor: "rgba(0,0,0,0.06)" }} />
        ) : (
          greeting
        )}
      </Box>

      {/* 3. Status pills */}
      {!botSessionInitLoading && (machine || hasBotSummary) && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            justifyContent: "center",
            mt: "16px",
            animation: "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.25s both",
          }}
        >
          {/* Service reports pill */}
          <Box sx={pillSx}>
            <ReportIcon />
            <Box component="span" sx={{ fontFamily: dt.fontMono, fontSize: "13px", fontWeight: 600, color: midas.gold }}>
              {openReports}
            </Box>
            <Box component="span" sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 400, color: midas.textGray }}>
              {translate("midas.welcome.pillReports")}
            </Box>
          </Box>

          {/* Service requests pill */}
          <Box sx={pillSx}>
            <ClockIcon />
            <Box component="span" sx={{ fontFamily: dt.fontMono, fontSize: "13px", fontWeight: 600, color: midas.srBlue }}>
              {activeSRs}
            </Box>
            <Box component="span" sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 400, color: midas.textGray }}>
              {translate("midas.welcome.pillRequests")}
            </Box>
          </Box>
        </Box>
      )}

      {/* 4. Recent Activity card */}
      {recentActivity && !botSessionInitLoading && (
        <Box
          sx={{
            width: "100%",
            mt: "16px",
            backgroundColor: "#FFFFFF",
            borderRadius: "10px",
            borderLeft: `3px solid ${midas.activityAmber}`,
            padding: "12px 16px",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            animation: "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.3s both",
          }}
        >
          <Box sx={{ flexShrink: 0, mt: "1px" }}>
            <WarningIcon />
          </Box>
          <Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 500,
                color: midas.textDark,
                mb: "2px",
              }}
            >
              {translate("midas.welcome.recentActivity")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 400,
                color: midas.textGray,
                lineHeight: "18px",
              }}
            >
              {recentActivity}
            </Box>
          </Box>
        </Box>
      )}

      {/* 5. Suggestion cards (2x2 grid) */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          width: "100%",
          mt: "20px",
        }}
      >
        {suggestions.map((item, index) => (
          <Box
            key={item.key}
            onClick={() => onSuggestionClick(item.text)}
            sx={{
              flex: "1 1 calc(50% - 4px)",
              minWidth: isPhone ? "100%" : "200px",
              backgroundColor: "#FFFFFF",
              border: `1px solid ${midas.pillBorder}`,
              borderRadius: "10px",
              padding: "12px 14px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s ease",
              animation: `midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) ${0.35 + index * 0.08}s both`,
              "&:hover": {
                borderColor: midas.gold,
                backgroundColor: "rgba(212, 168, 67, 0.04)",
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              },
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <SuggestionIcon />
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 400,
                color: midas.cardText,
                lineHeight: 1.4,
              }}
            >
              {item.text}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Shared pill style
const pillSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  backgroundColor: "#FFFFFF",
  borderRadius: "20px",
  border: `1px solid #E5E7EB`,
  padding: "6px 14px",
};

export default MidasMachineWelcome;
